import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation, withPreloading } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { routes } from './app.routes';
import { authInterceptor } from './core/auth/interceptors/auth.interceptor';
import { errorInterceptor } from './core/auth/interceptors/error.interceptor';
import { AuthService } from './core/auth/services/auth.service';
import { NetworkAwarePreloadingStrategy } from './core/router/network-aware-preloading.strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withEnabledBlockingInitialNavigation(), withPreloading(NetworkAwarePreloadingStrategy)),
    MessageService,
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: Aura,
      },
    }),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor])),
    // Démarre la restauration de session (GET /auth/me/bootstrap si un token est en localStorage)
    // SANS bloquer le bootstrap de l'app dessus. Les guards (auth/role/guestGuard) attendent déjà
    // auth.sessionRestored$ eux-mêmes avant de trancher un accès protégé — donc faire attendre TOUTE
    // l'app (y compris les pages publiques, où aucune décision d'accès ne dépend de la session) sur
    // cet appel réseau était une lenteur perçue pure, sans bénéfice de correction en échange.
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      auth.restoreSession().subscribe();
    }),
    provideTranslateService({
      lang: 'fr',
      fallbackLang: 'fr',
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
    }),
    // Sans cette attente, un composant qui résout un libellé via translate.instant() dans son
    // constructeur ou un initialiseur de champ (ex. un tableau de bord admin) peut s'exécuter avant
    // la fin du chargement HTTP du fichier de traduction sur un accès direct/rechargement : instant()
    // retourne alors la clé brute au lieu du texte français. On bloque le bootstrap jusqu'à la fin
    // du chargement de la langue par défaut pour que tout composant, même construit immédiatement,
    // trouve toujours le catalogue déjà prêt.
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      return firstValueFrom(translate.use('fr'));
    }),
    // Enregistré uniquement en production (voir ngsw-config.json + "serviceWorker" dans
    // angular.json, actif seulement pour la configuration "production"). Le délai de 30s laisse
    // l'app finir son chargement initial avant de solliciter le réseau pour le SW lui-même.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
